import { createQuote } from "@/api/store/QuotesService.js";
import { getOrder, updateOrder } from "@/api/store/OrdersService.js";
import {
  priceKeyOf,
  lineFor,
  emptyProductsUpdate,
  quoteResult,
  generalInfoPayload
} from "./quoteLines.js";

export default {
  definition: {
    name: "create_quote",
    description:
      "Crea una cotización nueva con las piezas, cantidades y el cliente que el vendedor eligió en las tarjetas (sin cliente si no eligió ninguno) y le muestra un enlace para abrirla. Con cliente trae su dirección, tipo de pago, condición y nivel de precio, y las piezas toman ese nivel. Devuelve la cotización como quedó guardada (el sistema puede recortar cantidades al stock y lo avisa en warnings), las piezas que no se pudieron agregar con el motivo y lo que le falta (missing). Si la conversación ya tiene cotización no crea otra: se actualiza con update_quote (syncSelected), salvo que el vendedor pida expresamente una cotización aparte (separate). Para el envío, quote_shipping.",
    strict: true,
    input_schema: {
      type: "object",
      properties: {
        name: {
          type: "string",
          description:
            "Nombre corto de la cotización si el vendedor lo dio; vacío si no"
        },
        separate: {
          type: "boolean",
          description:
            "true sólo si el vendedor pidió expresamente otra cotización aparte de la que ya tiene esta conversación"
        }
      },
      required: ["name", "separate"],
      additionalProperties: false
    }
  },

  async run({ name, separate }, ctx) {
    const { items, customer, quote } = ctx.getSelection();
    // Dos cotizaciones de lo mismo enredan al vendedor y al cliente
    if (quote && !separate) {
      throw new Error(
        `Esta conversación ya tiene la cotización ${quote.reference}: actualízala con update_quote (syncSelected) en lugar de crear otra. Sólo se crea otra si el vendedor la pide expresamente aparte.`
      );
    }
    if (!items.length) {
      throw new Error("El vendedor todavía no eligió ninguna pieza.");
    }

    // Sin cliente al crearla: quotes/create no carga los defaults del cliente
    // (dirección, condición, envío). Se le pone después como lo hace General
    // info del modal, que sí los carga, y antes de las piezas, para que éstas
    // tomen su nivel de precio.
    const { orderNumber } = await createQuote({ name });
    let order = await getOrder(orderNumber);
    let warnings = [];
    if (customer) {
      const res = await updateOrder(
        orderNumber,
        "general-info",
        generalInfoPayload(order, {
          customerInfoautoId: customer.personaId,
          customerEmail: customer.email || ""
        })
      );
      // Todavía no tiene piezas: "precios actualizados" no aplica
      warnings = ((res && res.warnings) || []).filter(
        warning => warning !== "Products prices updated"
      );
      order = await getOrder(orderNumber);
    }
    const priceKey = priceKeyOf(order);
    const departmentCode = order.departamentoCodigo || ctx.departmentCode;

    const results = await Promise.all(
      items.map(item =>
        lineFor(item.option.nkrCode, item.quantity, priceKey, departmentCode)
      )
    );
    const productsToAdd = results.filter(r => r.line).map(r => r.line);
    const skipped = results
      .map((result, index) => ({
        nkrCode: items[index].option.nkrCode,
        reason: result.skipped
      }))
      .filter(result => result.reason);

    if (productsToAdd.length) {
      const res = await updateOrder(orderNumber, "products-update", {
        ...emptyProductsUpdate(),
        productsToAdd
      });
      warnings = [...warnings, ...((res && res.warnings) || [])];
    }
    return quoteResult(orderNumber, ctx, { skipped, warnings });
  }
};
